import React from "react";
import Layout from "../components/Layout";
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  LifebuoyIcon,
  ClockIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/solid";

const ContactUsPage = () => {
  return (
    <Layout>
      <section id="contactus" className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero (no fixed heights) */}
        <div className="relative overflow-hidden rounded-2xl">
          <div
            className="absolute inset-0 -z-10 bg-cover bg-center"
            style={{ backgroundImage: 'url("/images/hero/contact.jpg")' }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 -z-10 bg-black/60" />
          <div className="px-6 sm:px-10 py-12 sm:py-16">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Contact KAR Shuttle</h1>
            <p className="mt-3 max-w-3xl text-sm sm:text-base text-gray-300">
              Questions about seats, departures, or gear space? We’re here to help. Book per-seat shuttles
              to Big White, SilverStar, Sun Peaks, and more — or ask about private charters.
            </p>

            {/* Quick actions (no forms) */}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="tel:+12505756041"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10"
              >
                <PhoneIcon className="h-5 w-5" />
                Call +1 (250) 575-6041
              </a>
              <a
                href="mailto:info@karrentals.ca?subject=KAR%20Shuttle%20Inquiry"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10"
              >
                <EnvelopeIcon className="h-5 w-5" />
                Email info@karrentals.ca
              </a>
              <a
                href="#support"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10"
              >
                <LifebuoyIcon className="h-5 w-5" />
                After-hours support
              </a>
            </div>
          </div>
        </div>

        {/* Info grid (no forms) */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* How to reach us */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/40 p-6">
            <h2 className="text-xl font-semibold">How to reach us</h2>
            <ul className="mt-3 space-y-3 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-white">Email:</strong>{" "}
                  <a className="underline hover:no-underline" href="mailto:info@karrentals.ca">
                    info@karrentals.ca
                  </a>
                </span>
              </li>
              <li className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-white">Phone:</strong>{" "}
                  <a className="underline hover:no-underline" href="tel:+12505756041">
                    +1 (250) 575-6041
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <InformationCircleIcon className="h-5 w-5 shrink-0" />
                <span>
                  Prefer messaging? Email is fastest for itinerary changes or special gear requests (roof boxes, child seats).
                </span>
              </li>
            </ul>
          </div>

          {/* Hours & response times */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/40 p-6">
            <h2 className="text-xl font-semibold">Hours & response times</h2>
            <ul className="mt-3 space-y-3 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-white">Business hours:</strong> 9:00 AM – 5:00 PM PT, 7 days a week
                </span>
              </li>
              <li className="flex items-start gap-2">
                <LifebuoyIcon className="h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-white">Day-of-travel support:</strong> If your departure is today,
                  call us for the fastest help.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <InformationCircleIcon className="h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-white">Typical reply times:</strong> Email replies within a few hours during business hours.
                </span>
              </li>
            </ul>
          </div>

          {/* Service area & pickup points */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/40 p-6">
            <h2 className="text-xl font-semibold">Service area & pickup points</h2>
            <ul className="mt-3 space-y-2 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <MapPinIcon className="h-5 w-5 shrink-0" />
                <span>
                  <strong className="text-white">Okanagan & Interior BC:</strong> Kelowna (YLW) ↔ Big White, Vernon ↔ SilverStar, Kamloops ↔ Sun Peaks, Penticton ↔ Apex, Revelstoke
                </span>
              </li>
            </ul>
            <div className="mt-4 text-xs text-gray-400">
              Exact pickup spots and timing are shown at booking. Please be ready 10 minutes before departure.
            </div>
          </div>
        </div>

        {/* Policies / FAQs */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-zinc-900/40 p-6">
          <h2 className="text-xl font-semibold">FAQs</h2>
          <p className="mt-2 text-sm text-gray-300">
            Find details on luggage limits, ski/board handling, cancellations, and child seats.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="/FAQS"
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 hover:bg-white/10"
            >
              FAQs
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* After-hours / emergencies (anchor target for quick action) */}
        <div id="support" className="mt-6 rounded-2xl border border-white/10 bg-zinc-900/40 p-6">
          <h2 className="text-xl font-semibold">After-hours & emergencies</h2>
          <p className="mt-2 text-sm text-gray-300">
            If you require assistance outside of business hours and you have an upcoming or in-progress ride,
            please call <a className="underline hover:no-underline" href="tel:+12505756041">+1 (250) 575-6041</a>.
            For roadside incidents, follow the instructions in your confirmation email/booking page.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default ContactUsPage;
